import currency from "currency.js";
import moment from "moment";
import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as Copy } from "../../../assets/svg/copy.svg";
import {
  GetTradeHistory200ResponseTradeHistoryInner,
  InstrumentTypeResponse,
  SideResponse,
  TradeTypeResponse,
} from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { SPACING } from "../../../constants/design/spacing";
import { nanosToSeconds } from "../../../utils/date";
import {
  formatCurrencyWithSubscript,
  formatSizePrecision,
} from "../../../utils/format";
import { getMarginTypeLabel } from "../../../utils/margin";
import { shortenString } from "../../../utils/strings";
import {
  getTradeExitReason,
  getTradeSide,
  hasNonZeroTradeValue,
} from "../../../utils/trade";
import CopyRow from "./CopyRow";
import { BaseModal } from "../../BaseModal";
import ShareButton from "../../Buttons/ShareButton";
import MarketCell from "../../PortfolioSettings/Table/MarketCell";
import { ViewDetailsContent, ViewDetailsRow } from "./style";
import { BoostValueWrapper } from "../../TradeForm/style";

interface ITradeDetailsModalContentProps {
  resolvedTrade?: GetTradeHistory200ResponseTradeHistoryInner;
  style?: CSSProperties;
  onHide?: () => void;
  amount_precision: number;
  price_precision: number;
  indexPrice?: string;
  grossPnl?: string;
  netPnl?: string;
  totalFees: string;
  positionFee: string;
  borrowingFee: string;
  fundingFee: string;
  showCopied: boolean;
  onCopyTradeId: () => void;
  onOpenPnLCard: () => void;
  renderSignedPnl: (value?: string) => JSX.Element;
  renderFee: (value?: string, signed?: boolean) => JSX.Element;
}

function TradeDetailsModalContent({
  resolvedTrade,
  style,
  onHide,
  amount_precision,
  price_precision,
  indexPrice,
  grossPnl,
  netPnl,
  totalFees,
  positionFee,
  borrowingFee,
  fundingFee,
  showCopied,
  onCopyTradeId,
  onOpenPnLCard,
  renderSignedPnl,
  renderFee,
}: ITradeDetailsModalContentProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "shared.ViewDetailsModal.TradeDetailsModal",
  });

  // Carries the "(TP Triggered)" / "(Liquidated)" suffix when the backend
  // reported why the position closed.
  const tradeSide = resolvedTrade
    ? getTradeSide(
        resolvedTrade.side,
        Boolean(resolvedTrade.is_closing),
        t,
        resolvedTrade.instrument_type === InstrumentTypeResponse.Spot,
        getTradeExitReason(resolvedTrade)
      )
    : undefined;

  return (
    <BaseModal
      title={
        resolvedTrade && (
          <MarketCell
            instrumentName={resolvedTrade.instrument_name}
            optionType={resolvedTrade.option_type}
            expiry={resolvedTrade.expiry}
            strike={resolvedTrade.strike}
          />
        )
      }
      show={!!resolvedTrade}
      onHide={onHide}
      style={{
        ...style,
        minWidth: 311,
      }}
    >
      {resolvedTrade && (
        <ViewDetailsContent>
          <ViewDetailsRow>
            <span>{t("side")}</span>
            <span
              style={{
                color:
                  tradeSide?.type === SideResponse.Buy
                    ? COLORS.up.one
                    : COLORS.down.one,
              }}
            >
              {tradeSide?.text}
            </span>
          </ViewDetailsRow>
          <ViewDetailsRow>
            <span>{t("type")}</span>
            <span>
              {resolvedTrade.instrument_type === InstrumentTypeResponse.Spot &&
                t("spot")}{" "}
              {t(String(resolvedTrade.trade_type).toLowerCase())}
            </span>
          </ViewDetailsRow>
          {resolvedTrade.settlement_price && (
            <ViewDetailsRow>
              <span>{t("settlement_price")}</span>
              <span>{currency(resolvedTrade.settlement_price).format()}</span>
            </ViewDetailsRow>
          )}
          <ViewDetailsRow>
            <span>{t("size")}</span>
            <span>
              {formatSizePrecision(resolvedTrade.amount, amount_precision)}
            </span>
          </ViewDetailsRow>
          <ViewDetailsRow>
            <span>{t("price")}</span>
            <span>
              {currency(resolvedTrade.price ?? 0, {
                precision: price_precision,
              }).format()}
            </span>
          </ViewDetailsRow>
          {resolvedTrade.order_type && (
            <ViewDetailsRow>
              <span>{t("order_type")}</span>
              <span>{t(String(resolvedTrade.order_type).toLowerCase())}</span>
            </ViewDetailsRow>
          )}
          {resolvedTrade.instrument_type !== InstrumentTypeResponse.Spot && (
            <ViewDetailsRow>
              <span>{t("margin_type", { defaultValue: "Margin Type" })}</span>
              <span>
                {getMarginTypeLabel(
                  (resolvedTrade as { margin_type?: string }).margin_type
                )}
              </span>
            </ViewDetailsRow>
          )}
          {resolvedTrade.instrument_type !== InstrumentTypeResponse.Spot && (
            <ViewDetailsRow>
              <span>{t("gross_pnl")}</span>
              {renderSignedPnl(grossPnl)}
            </ViewDetailsRow>
          )}
          {resolvedTrade.trade_type !== TradeTypeResponse.Settlement &&
            resolvedTrade.instrument_type !== InstrumentTypeResponse.Spot && (
            <ViewDetailsRow>
              <span>{t("index_price")}</span>
              <span>
                {indexPrice === undefined
                  ? "-"
                  : currency(indexPrice, {
                      precision: price_precision,
                    }).format()}
              </span>
            </ViewDetailsRow>
          )}
          {resolvedTrade.instrument_type !== InstrumentTypeResponse.Spot && (
            <>
              {hasNonZeroTradeValue(positionFee) && (
                <ViewDetailsRow>
                  <span>{t("position_fee")}</span>
                  {renderFee(positionFee)}
                </ViewDetailsRow>
              )}
              {hasNonZeroTradeValue(borrowingFee) && (
                <ViewDetailsRow>
                  <span>{t("borrowing_fee")}</span>
                  {renderFee(borrowingFee)}
                </ViewDetailsRow>
              )}
              {hasNonZeroTradeValue(fundingFee) && (
                <ViewDetailsRow>
                  <span>{t("funding_fee")}</span>
                  {renderFee(fundingFee, true)}
                </ViewDetailsRow>
              )}
            </>
          )}
          <ViewDetailsRow>
            <span>{t("total_fees")}</span>
            {renderFee(totalFees, resolvedTrade.instrument_type !== InstrumentTypeResponse.Spot)}
          </ViewDetailsRow>
          {resolvedTrade.liquidation_fee &&
            Number(resolvedTrade.liquidation_fee) !== 0 && (
            <ViewDetailsRow>
              <span>{t("liquidation_fee")}</span>
              <span>
                {formatCurrencyWithSubscript(resolvedTrade.liquidation_fee, {
                  precision: price_precision,
                })}
              </span>
            </ViewDetailsRow>
          )}
          {resolvedTrade.instrument_type !== InstrumentTypeResponse.Spot && (
            <ViewDetailsRow>
              <span>{t("net_pnl")}</span>
              {renderSignedPnl(netPnl)}
            </ViewDetailsRow>
          )}
          {resolvedTrade.farm_boost &&
            Number(resolvedTrade.farm_boost) > 0 && (
            <ViewDetailsRow
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <span>{t("farm_boost")}</span>
              <BoostValueWrapper>
                <span>
                  {!resolvedTrade.farm_boost
                    ? "-"
                    : `${currency(resolvedTrade.farm_boost, {
                        symbol: "",
                      }).format()}x`}
                </span>
              </BoostValueWrapper>
            </ViewDetailsRow>
          )}
          <ViewDetailsRow>
            <span>{t("date_time")}</span>
            <span>
              {moment
                .unix(nanosToSeconds(resolvedTrade.created_timestamp))
                .format("HH:mm:ss - DD MMM YY")}
            </span>
          </ViewDetailsRow>
          {resolvedTrade.trade_id && (
            <ViewDetailsRow clickable onClick={onCopyTradeId}>
              <span>{t("trade_id")}</span>
              <span>
                <Copy style={{ marginRight: SPACING.two }} />
                {showCopied
                  ? t("copied_trade_id")
                  : shortenString(resolvedTrade.trade_id, 5, 5)}
              </span>
            </ViewDetailsRow>
          )}
          <CopyRow
            label={t("transaction", { defaultValue: "Transaction" })}
            value={(resolvedTrade as { tx_hash?: string }).tx_hash}
          />
          {Number(netPnl) && Boolean(resolvedTrade.is_closing) ? (
            <ShareButton
              fullWidth
              onClick={onOpenPnLCard}
              style={{ marginTop: SPACING.three }}
            />
          ) : undefined}
        </ViewDetailsContent>
      )}
    </BaseModal>
  );
}

export default TradeDetailsModalContent;
